'use client';

import { useReducedMotion } from 'framer-motion';
import type { CSSProperties } from 'react';
import type { AvatarFrameId } from '@/lib/api/rewards-hub/models';
import { LottieIllustration } from '@/lib/ui-kit/lottie-illustration';
import { cn } from '@/lib/ui-kit/utils';
import { AVATAR_FALLBACK, AVATAR_SPARKS, AVATAR_WREATH_ART, avatarGlowRing } from './avatarTheme';

/**
 * Below this the twinkle is too small to read, and a header or table avatar
 * would load the Lottie player on every page for it: the sparkles stay still.
 */
const SPARKLE_ANIMATION_MIN_SIZE = 64;

const NO_FRAMES: readonly AvatarFrameId[] = [];

/**
 * A player's portrait wearing its frames (Figma 13878:183036) — the ring, the
 * sparkles and the wreath stack, each drawn on its own. Every measure inside
 * is a ratio of the box, so one component serves the 42px swatch, the 84px
 * grid cell and the 120px hero without three sets of numbers.
 */

interface AvatarPortraitProps {
  src: string | null;
  /** Box size in px. Overridable per breakpoint from `className`. */
  size?: number;
  /** The frames worn over it, any of them at once. */
  frames?: readonly AvatarFrameId[];
  /** Not yet earned: the design greys it out and drops a padlock on it. */
  locked?: boolean;
  /** The one currently worn — a bright rim, as the Selected state draws it. */
  selected?: boolean;
  /**
   * Just earned: starts in the locked look and lifts out of it on the row
   * unlock's beat (CSS `rewards-avatar-unlock`, see AvatarPickerDialog).
   */
  unlocking?: boolean;
  alt?: string;
  className?: string;
}

export function AvatarPortrait({
  src,
  size = 84,
  frames = NO_FRAMES,
  locked = false,
  selected = false,
  unlocking = false,
  alt = '',
  className,
}: AvatarPortraitProps) {
  const reduceMotion = useReducedMotion();
  const twinkle = !reduceMotion && size >= SPARKLE_ANIMATION_MIN_SIZE;
  // A locked portrait is a teaser of the art alone; frames are for the worn one.
  const glow = !locked && frames.includes('GLOW');
  const sparkles = !locked && frames.includes('SPARKLES');
  const wreath = !locked && frames.includes('WREATH');
  return (
    <span
      className={cn('relative inline-block size-(--avatar-size) shrink-0 rounded-full', className)}
      style={{ '--avatar-size': `${size}px` } as CSSProperties}
    >
      <span
        className={cn(
          'block size-full overflow-hidden rounded-full',
          locked && 'opacity-40 grayscale',
          unlocking && 'rewards-avatar-unlock',
        )}
        style={
          glow
            ? { boxShadow: avatarGlowRing(size) }
            : selected
              ? { boxShadow: '0 0 0 2px var(--v2-btn-primary)' }
              : undefined
        }
      >
        <img
          loading="lazy"
          decoding="async"
          src={src || AVATAR_FALLBACK}
          alt={alt}
          draggable={false}
          className="size-full object-cover"
        />
      </span>

      {wreath ? (
        // Figma sets it across the foot of the portrait: 92.5% of its width,
        // its top edge at 84.4% of the height, hanging below the rim.
        <img
          loading="lazy"
          decoding="async"
          src={AVATAR_WREATH_ART}
          alt=""
          aria-hidden
          draggable={false}
          className="pointer-events-none absolute top-[84.4%] left-1/2 w-[92.5%] max-w-none -translate-x-1/2"
        />
      ) : null}

      {/* Last, so a spark that meets the wreath's wing shows over it. */}
      {sparkles && twinkle ? (
        // The sparkles twinkle, one after another, slow and soft (05.10, the
        // designer's avatar_sparkles Lottie). Figma draws it on a 148 px box
        // round the 120 px portrait: 23% wider, 11.7% out on every side.
        <LottieIllustration name="avatar-sparkles" loop fill className="absolute -inset-[11.67%]" />
      ) : null}
      {sparkles && !twinkle
        ? AVATAR_SPARKS.map((spark) => (
            <img
              loading="lazy"
              decoding="async"
              key={spark.src}
              src={spark.src}
              alt=""
              aria-hidden
              draggable={false}
              className="pointer-events-none absolute max-w-none"
              style={{
                top: spark.top,
                left: spark.left,
                width: `${spark.size * 100}%`,
                height: `${spark.size * 100}%`,
              }}
            />
          ))
        : null}
    </span>
  );
}
